import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { AuthShell } from "../components/AuthShell";
import { PhoneShowcase } from "../components/PhoneShowcase";
import { Button } from "../components/ui/Button";
import { DURATION, EASE_OUT_SOFT } from "../lib/motion";

/**
 * Beyond Sure — Phone OTP Verification Screen.
 *
 * Sister to OtpScreen.tsx — identical 6-digit cell pattern, identical
 * paste/backspace/auto-advance, identical resend cooldown. Differences are
 * purely backend:
 *   • verify endpoint   /api/auth/phone/verify-otp  (not /api/otp/verify)
 *   • resend endpoint   /api/auth/phone/send-otp    (not /api/otp/send)
 *   • request payload   { phone, code, agreed_to_terms } where phone is the
 *                       10-digit normalized number from PhoneSignInScreen
 *
 * Success path hydrates the SAME localStorage keys as email OTP + Google
 * OAuth (bsure_token + ig_t + ig_u + bsure_email) so the rest of the app
 * doesn't care which channel auth'd the user. The user payload from the
 * phone endpoint may not include an email (phone-only signups have a
 * blank email until the user adds one later), so bsure_email is only set
 * when present.
 *
 * Like its email twin, this whole screen sits behind PHONE_LOGIN_ENABLED —
 * nothing here renders in production until the SMS provider key lands.
 */

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_S = 30;

interface PhoneOtpScreenProps {
  /** Normalized 10-digit number from PhoneSignInScreen. */
  phone: string;
  /** Fires on successful /api/auth/phone/verify-otp. `isNew` distinguishes
   *  a fresh signup (route to onboarding) from a returning sign-in (route
   *  straight to /chat) — same shape as the email OtpScreen contract. */
  onVerified: (result: { isNew: boolean }) => void;
  onBack: () => void;
}

function ArrowRightGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PhoneOtpScreen({ phone, onVerified, onBack }: PhoneOtpScreenProps) {
  const [code, setCode] = useState<string[]>(() => Array(CODE_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_S);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  // AY3 (AV G2 parity): one-shot guard so a complete code verifies exactly once.
  const submittingRef = useRef(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const filled = useMemo(() => code.every((c) => /^\d$/.test(c)), [code]);

  function setDigit(idx: number, value: string) {
    if (!/^\d?$/.test(value)) return;
    const next = [...code];
    next[idx] = value;
    setCode(next);
    setErrored(false);
    if (value && idx < CODE_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
    // AY3 (AV G1 parity): typing the final digit auto-submits, matching legacy.
    if (next.every((c) => /^\d$/.test(c))) void runVerify(next.join(""));
  }

  function handleKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      if (code[idx]) return;
      if (idx > 0) {
        e.preventDefault();
        const next = [...code];
        next[idx - 1] = "";
        setCode(next);
        inputRefs.current[idx - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      inputRefs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < CODE_LENGTH - 1) {
      e.preventDefault();
      inputRefs.current[idx + 1]?.focus();
    } else if (e.key === "Enter" && filled) {
      e.preventDefault();
      handleSubmit();
    }
  }

  function handlePaste(idx: number, e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData("text").trim();
    const digits = text.replace(/\D/g, "");
    if (!digits) return;
    e.preventDefault();
    const next = [...code];
    for (let i = 0; i < CODE_LENGTH; i++) next[i] = digits[i] ?? "";
    setCode(next);
    setErrored(false);
    const lastFilled = Math.min(idx + digits.length, CODE_LENGTH) - 1;
    inputRefs.current[Math.min(lastFilled + 1, CODE_LENGTH - 1)]?.focus();
    // AY3 (AV G1 parity): a full pasted code auto-submits, matching the type path.
    if (next.every((c) => /^\d$/.test(c))) void runVerify(next.join(""));
  }

  // AY3: verify with the code passed EXPLICITLY (so the auto-submit paths use the
  // fresh value); submittingRef is the AV G2 double-submit guard.
  async function runVerify(fullCode: string) {
    if (fullCode.length !== CODE_LENGTH || submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    setErrored(false);
    // POST /api/auth/phone/verify-otp. agreed_to_terms is sent unconditionally
    // for the same reason as the email OTP screen: the only path here runs
    // through PhoneSignInScreen which already gates on the T&C checkbox.
    try {
      const r = await fetch("/api/auth/phone/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, code: fullCode, agreed_to_terms: true }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data?.token || !data?.user) {
        setErrored(true);
        setLoading(false);
        submittingRef.current = false; // recoverable — allow a corrected retry
        return;
      }
      // Hydrate both storage conventions — same keys + same shape as the
      // email OTP and Google-OAuth paths. Email may be empty on phone-only
      // signups; only persist bsure_email when the server returned one.
      window.localStorage.setItem("bsure_token", data.token);
      window.localStorage.setItem("ig_t", data.token);
      window.localStorage.setItem("ig_u", JSON.stringify(data.user));
      if (data.user.email) {
        window.localStorage.setItem("bsure_email", data.user.email);
      }
      setLoading(false);
      onVerified({ isNew: Boolean(data.user.is_new) });
    } catch {
      setErrored(true);
      setLoading(false);
      submittingRef.current = false; // network blip — allow a retry
    }
  }

  // Manual submit (Verify button, Enter key, form submit) → delegates to runVerify.
  function handleSubmit(e?: React.FormEvent) {
    if (e) e.preventDefault();
    void runVerify(code.join(""));
  }

  async function handleResend() {
    if (cooldown > 0) return;
    try {
      await fetch("/api/auth/phone/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
    } catch {
      /* network blip — cooldown still resets so the UI gives feedback */
    }
    setCooldown(RESEND_COOLDOWN_S);
  }

  return (
    <AuthShell
      eyebrow="Sign in · Free 7-day trial"
      headline="Insure smarter, live better."
      subhead="BeyondSure is India's most intelligent AI insurance advisor — in English, Hindi, and 10+ Indian languages."
      formTitle="Enter your code."
      showcase={<PhoneShowcase />}
    >
      <motion.form
        onSubmit={handleSubmit}
        noValidate
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: DURATION.base, ease: EASE_OUT_SOFT }}
        className="bsure-ui flex flex-col gap-5"
      >
        <p className="bsure-prose text-[13.5px] leading-[1.55] text-[var(--color-ink-60)]">
          We sent a 6-digit code over SMS to{" "}
          <span className="text-[var(--color-ink)] font-medium">
            +91 {phone}
          </span>
          . It usually arrives within a minute.
        </p>

        <div
          className="flex items-center gap-2 sm:gap-2.5"
          role="group"
          aria-label="One-time passcode"
        >
          {code.map((digit, i) => (
            <input
              key={i}
              ref={(el) => {
                inputRefs.current[i] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              pattern="[0-9]*"
              aria-label={`Digit ${i + 1} of ${CODE_LENGTH}`}
              value={digit}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={(e) => handlePaste(i, e)}
              className={[
                "h-12 w-10 sm:h-14 sm:w-12 text-center",
                "bsure-display text-[22px] sm:text-[26px] text-[var(--color-ink)]",
                "bg-[var(--color-paper)]",
                "rounded-[var(--radius-md)]",
                "border-[1.5px] outline-none",
                "transition-[border-color,box-shadow] duration-[var(--duration-base)]",
                errored
                  ? "border-[var(--color-ink)]"
                  : "border-[var(--color-ink-16)] focus:border-[var(--color-brand)] focus:shadow-[0_0_0_4px_rgb(62_142_91_/_0.12)]",
              ].join(" ")}
              style={{ fontWeight: 700 }}
            />
          ))}
        </div>

        {errored ? (
          <p className="text-[12.5px] text-[var(--color-ink)] font-medium">
            That code didn't match. Try again or resend a new one.
          </p>
        ) : null}

        <div className="flex items-center justify-between text-[12.5px]">
          <span className="text-[var(--color-ink-60)]">
            Didn't get it?{" "}
            {cooldown > 0 ? (
              <span className="text-[var(--color-ink-40)]">
                Resend in {cooldown}s
              </span>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                className="text-[var(--color-brand-deep)] underline underline-offset-2 hover:text-[var(--color-ink)] transition-colors font-medium"
              >
                Resend code
              </button>
            )}
          </span>
        </div>

        <Button
          type="submit"
          variant="primary"
          fullWidth
          loading={loading}
          disabled={!filled}
          trailing={<ArrowRightGlyph />}
        >
          Verify
        </Button>

        <button
          type="button"
          onClick={onBack}
          className="bsure-ui mt-1 text-[12.5px] text-[var(--color-ink-60)] hover:text-[var(--color-ink)] transition-colors self-start"
        >
          ← Use a different number
        </button>
      </motion.form>
    </AuthShell>
  );
}

export default PhoneOtpScreen;
